/**
 * Centralised access to Vite env vars.
 *
 * This is the ONLY module in the frontend that reads `import.meta.env`. All
 * other modules import from `config` so:
 *   - the set of supported env vars is discoverable in one place;
 *   - typos in env-var names fail at build time, not silently at runtime;
 *   - tests can swap the export by mocking this module.
 */

type UnitSystem = "metric" | "imperial";

function readString(key: string): string | undefined {
  const v = import.meta.env[key];
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function readFloat(key: string, fallback: number): number {
  const v = readString(key);
  if (v === undefined) return fallback;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

function readBool(key: string): boolean {
  return readString(key) === "true";
}

export interface SentryConfig {
  /** Empty string disables Sentry entirely. */
  dsn: string;
  environment: string;
  release: string | undefined;
  /** 0..1. 0 disables performance tracing. */
  tracesSampleRate: number;
}

export const sentryConfig: SentryConfig = {
  dsn: readString("VITE_SENTRY_DSN") ?? "",
  environment: readString("VITE_SENTRY_ENVIRONMENT") ?? "development",
  release: readString("VITE_SENTRY_RELEASE"),
  tracesSampleRate: readFloat("VITE_SENTRY_TRACES_SAMPLE_RATE", 0.1),
};

export const unitSystem: UnitSystem =
  readString("VITE_UNITS") === "metric" ? "metric" : "imperial";

export const debugEnabled: boolean = readBool("VITE_DEBUG");
